import { Container, Graphics, Text, TextStyle } from "pixi.js";
import type { GridLineKind, MeasureGrid } from "./grid-lines";

/**
 * La règle graduée du bord gauche du roll.
 *
 * Les lignes de mesure (`Measures`) traversent toute la largeur et disent
 * *quand* : elles sont la trame sur laquelle les notes tombent. La règle, elle,
 * dit *où l'on en est* — un trait long par mesure, un moyen par temps, un court
 * par demi-temps, serrés dans une gouttière de quelques pixels sur le bord. C'est le repère
 * qu'on lit du coin de l'oeil, sans quitter les notes des yeux, et il tient
 * même quand la trame est éteinte : hors métronome les lignes de mesure
 * tombent à 0,12 d'opacité, à la limite du visible.
 *
 * # Elle s'ouvre sous le pointeur
 *
 * Au repos elle tient dans {@link REST_WIDTH} pixels et n'écrit rien : des
 * numéros permanents seraient du bruit sur la surface la plus chargée de
 * l'app — quatre secondes de notes qui tombent, les accords à gauche, la
 * tonalité à droite. Sous le pointeur posé sur sa gouttière (voir
 * {@link RULER_HIT_WIDTH}), et sous le doigt qui s'y pose, elle s'ouvre à
 * {@link OPEN_WIDTH} sur un fond violet : les graduations glissent vers
 * l'intérieur du roll, les numéros de mesure viennent à gauche, et la place
 * qui s'ouvre entre les deux est celle où se pose le repère
 * (`RecordMarker`).
 *
 * Tout se dérive de la largeur courante du panneau, elle-même animée : c'est
 * elle seule qui bouge, les trois colonnes suivent. Pendant l'animation le
 * composant se déclare occupé, ce qui garde le ticker éveillé — sans ça la
 * boucle se parke au milieu du mouvement.
 */

type MeasureRulerProps = {
  fromTick: number;
  toTick: number;
  height: number;
  /** Le pointeur est sur la gouttière : la règle s'ouvre. */
  expanded: boolean;
  /**
   * Les numéros des mesures qui s'écrivent à l'accent — celles qui contiennent
   * les bornes de la zone d'enregistrement. `null` quand la borne n'est pas
   * posée.
   */
  accentBarStart: number | null;
  accentBarEnd: number | null;
  /** Delta d'horloge murale depuis la dernière image, pour l'ouverture. */
  deltaMS: number;
};

/** Largeur de la gouttière au repos : juste les graduations. */
const REST_WIDTH = 22;
/** Largeur du panneau ouvert : les numéros, la place du repère, les graduations. */
const OPEN_WIDTH = 80;

/** Durée caractéristique de l'ouverture (ms). */
const EXPAND_MS = 110;

const BAR_LENGTH = 16;
/**
 * Un pixel, comme les autres graduations.
 *
 * Elle en faisait deux, et c'est la longueur qui dit déjà la mesure — 16 px
 * contre 7 et 4, à trois opacités distinctes. Deux pixels, en revanche, lui
 * faisaient encadrer les lignes d'un pixel qui passent au même endroit : celle
 * de l'accord, au-dessus d'elle dans la pile, dépassait d'un demi-pixel de
 * chaque côté et se retrouvait *sertie* par la graduation qu'elle était censée
 * couvrir. Passer les deux à la même épaisseur suffit à ce que l'accord gagne.
 */
const BAR_THICKNESS = 1;
const BEAT_LENGTH = 7;
const BEAT_THICKNESS = 1;
/**
 * Le demi-temps, plus petite graduation de la règle, et donc le pas du repère.
 *
 * Court et pâle : c'est une texture, pas une information — on ne le lit pas, on
 * s'y pose. Il n'existe que pour donner au repère un cran où tomber entre deux
 * temps, ce qui est exactement pourquoi il se relève quand le panneau s'ouvre :
 * c'est là qu'on vise.
 */
const SUBDIV_LENGTH = 4;
const SUBDIV_THICKNESS = 1;

/**
 * Distance entre le bord droit du panneau et le départ des graduations.
 *
 * C'est l'ancre de tout le glissement : les traits sont posés à `largeur - 16`,
 * donc au repos ils commencent à 6 px du bord de l'écran et, panneau ouvert, à
 * 64 px — collés à l'intérieur du roll, là où tombent les notes qu'ils datent.
 */
const TICKS_FROM_RIGHT = 16;

/**
 * Bord gauche des numéros de mesure.
 *
 * Fixe, quand tout le reste du panneau glisse : c'est la seule colonne qui a
 * un vis-à-vis hors de la règle — les noms d'accords, posés au même retrait de
 * 8 px par `Harmony`. Les deux textes se lisent l'un sous l'autre de part et
 * d'autre de la ligne, donc ils partagent leur bord gauche ; un numéro qui
 * glisserait pendant l'ouverture casserait cet alignement à chaque image.
 *
 * À garder d'accord avec `LABEL_PAD_LEFT` dans `harmony.ts`.
 */
const NUMBER_X = 8;

/**
 * Largeur de la zone qui **ouvre** la règle.
 *
 * Plus large que la gouttière au repos : c'est une cible tactile, et le pouce
 * vise le bord de l'écran sans précision. Elle ne dit pas ce qui la *garde*
 * ouverte — voir {@link RULER_OPEN_WIDTH}.
 */
export const RULER_HIT_WIDTH = 40;

/**
 * Largeur du panneau ouvert, qui est aussi ce qui le garde ouvert.
 *
 * Entrer et sortir ne se font pas au même endroit, et c'est nécessaire : la
 * zone d'entrée est plus étroite que le panneau qu'elle déploie, donc un
 * curseur qui avance de 45 px l'aurait ouvert puis refermé aussitôt, alors
 * qu'il est posé en plein dessus. On entre à {@link RULER_HIT_WIDTH}, on ne
 * sort qu'au bord de ce qu'on a ouvert.
 */
export const RULER_OPEN_WIDTH = OPEN_WIDTH;

const TICK_COLOR = 0xffffff;
/** Le violet du studio, celui de la marge de tonalité et du do central. */
const ACCENT = 0xa78bfa;
const ACCENT_BRIGHT = 0xc4b5fd;

/** Opacité du fond violet, panneau grand ouvert. */
const PANEL_ALPHA = 0.14;
/** Le filet qui ferme le panneau à droite, et le sépare du roll. */
const PANEL_EDGE_ALPHA = 0.3;

/**
 * Opacités des graduations, au repos et panneau ouvert.
 *
 * Au repos la règle est un repère périphérique et se tient sous le seuil des
 * lignes de mesure du métronome (0,4 / 0,34) : elle ne doit pas passer devant
 * ce qui porte la pulsation. Ouverte, elle se relève — on la regarde, elle a
 * le droit d'être nette.
 */
const BAR_ALPHA = 0.32;
const BAR_ALPHA_OPEN = 0.62;
const BEAT_ALPHA = 0.14;
const BEAT_ALPHA_OPEN = 0.26;
const SUBDIV_ALPHA = 0.07;
const SUBDIV_ALPHA_OPEN = 0.18;

const NUMBER_FONT_SIZE = 10;
const NUMBER_ALPHA = 0.8;
/**
 * De combien le numéro descend sous sa graduation.
 *
 * Sous, et non centré dessus : le dessus de chaque ligne appartient déjà au nom
 * de l'accord qui commence là, et deux textes qui se disputent la même bande de
 * quelques pixels se lisent mal tous les deux. Chaque ligne se lit donc de haut
 * en bas — l'accord, le trait, la mesure.
 */
const NUMBER_BELOW = 3;

const LENGTH_OF: Record<GridLineKind, number> = {
  bar: BAR_LENGTH,
  beat: BEAT_LENGTH,
  subdiv: SUBDIV_LENGTH,
};

const THICKNESS_OF: Record<GridLineKind, number> = {
  bar: BAR_THICKNESS,
  beat: BEAT_THICKNESS,
  subdiv: SUBDIV_THICKNESS,
};

const REST_ALPHA_OF: Record<GridLineKind, number> = {
  bar: BAR_ALPHA,
  beat: BEAT_ALPHA,
  subdiv: SUBDIV_ALPHA,
};

const OPEN_ALPHA_OF: Record<GridLineKind, number> = {
  bar: BAR_ALPHA_OPEN,
  beat: BEAT_ALPHA_OPEN,
  subdiv: SUBDIV_ALPHA_OPEN,
};

export class MeasureRuler {
  private container: Container;
  private panel: Graphics;
  private panelEdge: Graphics;

  private pool: Graphics[] = [];
  private poolIndex = 0;
  /** Géométrie cuite dans chaque `Graphics` du bassin ; `undefined` = vierge. */
  private poolKind: (GridLineKind | undefined)[] = [];

  private textPool: Text[] = [];
  private textPoolIndex = 0;
  private textPoolNumber: number[] = [];

  /** Ouverture courante, de 0 (gouttière) à 1 (panneau). */
  private expansion = 0;
  private expansionTarget = 0;

  private previousProps?: MeasureRulerProps;

  private highWaterMark = 0;
  private textHighWaterMark = 0;
  private framesSinceLastShrink = 0;

  constructor(private stage: Container, private grid: MeasureGrid) {
    this.container = new Container();
    this.stage.addChild(this.container);

    // Fond et filet sont cuits à l'unité et mis à l'échelle : leur taille
    // change à chaque image de l'ouverture, et redessiner un rectangle
    // soixante fois par seconde pour en changer la largeur retesselle pour
    // rien.
    this.panel = new Graphics();
    this.panel.rect(0, 0, 1, 1);
    this.panel.fill({ color: ACCENT, alpha: 1 });
    this.panel.visible = false;
    this.container.addChild(this.panel);

    this.panelEdge = new Graphics();
    this.panelEdge.rect(0, 0, 1, 1);
    this.panelEdge.fill({ color: ACCENT_BRIGHT, alpha: 1 });
    this.panelEdge.visible = false;
    this.container.addChild(this.panelEdge);
  }

  /** Largeur occupée à cet instant, animation comprise. */
  get panelWidth(): number {
    return REST_WIDTH + (OPEN_WIDTH - REST_WIDTH) * this.expansion;
  }

  /** L'ouverture, de 0 à 1. Le repère s'en sert pour suivre le mouvement. */
  get openness(): number {
    return this.expansion;
  }

  /** L'ouverture est-elle encore en train de bouger ? */
  isAnimating(): boolean {
    return this.expansion !== this.expansionTarget;
  }

  /** Repeindre la prochaine image même si les props n'ont pas bougé. */
  invalidate(): void {
    this.previousProps = undefined;
  }

  destroy(): void {
    for (const g of this.pool) {
      g.destroy();
    }

    for (const t of this.textPool) {
      t.destroy();
    }

    this.pool.length = 0;
    this.poolIndex = 0;
    this.poolKind.length = 0;
    this.textPool.length = 0;
    this.textPoolIndex = 0;
    this.textPoolNumber.length = 0;

    this.container.destroy({ children: true });
  }

  draw(props: MeasureRulerProps): void {
    this.advanceExpansion(props);

    if (this.shouldSkip(props)) {
      return;
    }

    const { fromTick, toTick, height, accentBarStart, accentBarEnd } = props;
    const tickRange = toTick - fromTick;

    this.previousProps = { ...props };
    this.resetPool();
    this.resetTextPool();

    if (tickRange <= 0 || height <= 0) {
      this.panel.visible = false;
      this.panelEdge.visible = false;

      return;
    }

    const width = this.panelWidth;
    const open = this.expansion;

    this.panel.scale.set(width, height);
    this.panel.alpha = PANEL_ALPHA * open;
    this.panel.visible = open > 0.001;

    this.panelEdge.x = width - 1;
    this.panelEdge.scale.set(1, height);
    this.panelEdge.alpha = PANEL_EDGE_ALPHA * open;
    this.panelEdge.visible = this.panel.visible;

    const tickX = width - TICKS_FROM_RIGHT;
    const scale = height / tickRange;
    const lines = this.grid.linesFor(fromTick, toTick);

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.tick < fromTick || line.tick > toTick) {
        continue;
      }

      const y = height - (line.tick - fromTick) * scale;
      const isBar = line.kind === 'bar';

      const poolIdx = this.poolIndex;
      const g = this.getOrCreateTick();

      if (this.poolKind[poolIdx] !== line.kind) {
        this.bakeTick(g, line.kind);
        this.poolKind[poolIdx] = line.kind;
      }

      g.x = tickX;
      g.y = y;
      g.alpha = this.tickAlpha(line.kind, open);
      g.visible = true;

      // Les numéros n'existent que dans le panneau ouvert : au repos il n'y a
      // pas la place, et c'est très bien — la règle y est un repère, pas une
      // lecture.
      if (open <= 0.01 || !isBar) {
        continue;
      }

      const textIdx = this.textPoolIndex;
      const t = this.getOrCreateNumber();

      if (this.textPoolNumber[textIdx] !== line.barNumber) {
        t.text = String(line.barNumber);
        this.textPoolNumber[textIdx] = line.barNumber;
      }

      // Les mesures que les bornes tiennent s'écrivent à l'accent : ce sont
      // les seuls numéros qu'on cherche vraiment quand il y en a. Par le
      // numéro et non par le tick : une borne se pose au demi-temps, donc elle
      // tombe le plus souvent *entre* deux barres, et c'est celle qui la
      // contient qu'on accentue.
      const accented = line.barNumber === accentBarStart
        || line.barNumber === accentBarEnd;

      t.style.fill = accented ? ACCENT_BRIGHT : TICK_COLOR;
      t.x = NUMBER_X;
      t.y = y + NUMBER_BELOW;
      t.alpha = (accented ? 1 : NUMBER_ALPHA) * open;
      t.visible = true;
    }

    if (this.poolIndex > this.highWaterMark) {
      this.highWaterMark = this.poolIndex;
    }

    if (this.textPoolIndex > this.textHighWaterMark) {
      this.textHighWaterMark = this.textPoolIndex;
    }

    this.framesSinceLastShrink++;

    if (this.framesSinceLastShrink >= 300) {
      this.shrinkPool();
      this.framesSinceLastShrink = 0;
    }
  }

  /**
   * Avancer l'ouverture d'une image.
   *
   * Approche exponentielle, calée sur l'horloge murale et non sur le nombre
   * d'images : la boucle du studio tourne à des cadences très différentes
   * selon la machine, et une constante par image ouvrirait la règle deux fois
   * plus vite sur un écran à 120 Hz.
   */
  private advanceExpansion(props: MeasureRulerProps): void {
    this.expansionTarget = props.expanded ? 1 : 0;

    if (this.expansion === this.expansionTarget) {
      return;
    }

    const k = Math.min(1, props.deltaMS / EXPAND_MS);
    const next = this.expansion + (this.expansionTarget - this.expansion) * k;

    // Sans ce collage l'approche exponentielle n'arrive jamais : elle
    // laisserait le composant se déclarer animé pour l'éternité, et le ticker
    // avec lui.
    this.expansion = Math.abs(this.expansionTarget - next) < 0.002
      ? this.expansionTarget
      : next;
  }

  private shouldSkip(props: MeasureRulerProps): boolean {
    const prev = this.previousProps;

    if (!prev || this.isAnimating()) {
      return false;
    }

    return prev.fromTick === props.fromTick
      && prev.toTick === props.toTick
      && prev.height === props.height
      && prev.expanded === props.expanded
      && prev.accentBarStart === props.accentBarStart
      && prev.accentBarEnd === props.accentBarEnd;
  }

  /**
   * Cuire la géométrie d'une graduation, une fois pour toutes.
   *
   * Elle ne dépend que de son espèce — ni la largeur du roll ni l'ouverture ne
   * l'atteignent, le panneau la déplace sans la déformer — donc seul `x` / `y`
   * bouge d'une image à l'autre, et Pixi réutilise le maillage au lieu de le
   * retesseler soixante fois par seconde.
   */
  private bakeTick(g: Graphics, kind: GridLineKind): void {
    const length = LENGTH_OF[kind];
    const thickness = THICKNESS_OF[kind];

    g.clear();
    g.rect(0, -thickness / 2, length, thickness);
    g.fill({ color: TICK_COLOR, alpha: 1 });
  }

  /** L'opacité d'une graduation, interpolée entre gouttière et panneau. */
  private tickAlpha(kind: GridLineKind, open: number): number {
    const rest = REST_ALPHA_OF[kind];

    return rest + (OPEN_ALPHA_OF[kind] - rest) * open;
  }

  private resetPool(): void {
    for (let i = 0; i < this.poolIndex; i++) {
      this.pool[i].visible = false;
    }

    this.poolIndex = 0;
  }

  private resetTextPool(): void {
    for (let i = 0; i < this.textPoolIndex; i++) {
      this.textPool[i].visible = false;
    }

    this.textPoolIndex = 0;
  }

  private shrinkPool(): void {
    const targetSize = Math.ceil(this.highWaterMark * 1.2);

    if (this.pool.length > targetSize) {
      for (let i = targetSize; i < this.pool.length; i++) {
        this.pool[i].destroy();
      }

      this.pool.length = targetSize;
      this.poolKind.length = targetSize;
    }

    const textTargetSize = Math.ceil(this.textHighWaterMark * 1.2);

    if (this.textPool.length > textTargetSize) {
      for (let i = textTargetSize; i < this.textPool.length; i++) {
        this.textPool[i].destroy();
      }

      this.textPool.length = textTargetSize;
      this.textPoolNumber.length = textTargetSize;
    }

    this.highWaterMark = 0;
    this.textHighWaterMark = 0;
  }

  private getOrCreateTick(): Graphics {
    if (this.poolIndex < this.pool.length) {
      return this.pool[this.poolIndex++];
    }

    const g = new Graphics();
    this.container.addChild(g);
    this.pool.push(g);
    this.poolKind.push(undefined);
    this.poolIndex++;

    return g;
  }

  private getOrCreateNumber(): Text {
    if (this.textPoolIndex < this.textPool.length) {
      return this.textPool[this.textPoolIndex++];
    }

    const t = new Text({
      text: "",
      style: new TextStyle({
        fontFamily: "system-ui, sans-serif",
        fontSize: NUMBER_FONT_SIZE,
        fill: TICK_COLOR,
      }),
    });

    // Ancré en haut à gauche : à gauche pour partager son bord avec le nom
    // d'accord qui se lit juste au-dessus de la même ligne, en haut pour pendre
    // sous sa graduation au lieu de l'enjamber.
    t.anchor.set(0, 0);
    this.container.addChild(t);
    this.textPool.push(t);
    this.textPoolNumber.push(-1);
    this.textPoolIndex++;

    return t;
  }
}
